import {
  MANAGE_MEMBER_GROUPS_NO_GROUPS,
  MANAGE_MEMBER_GROUPS_TITLE
} from '@bo-constants/member/memberCopy';
import { Button } from '@library/components/buttons';
import { Combobox } from '@library/components/selects';
import GrayTrashIcon from '@library/assets/images/icons/gray-trash-icon.svg';
import GreenCheckIcon from '@library/assets/images/icons/checked-green-icon.svg';
import { Loading } from '@library/components/Loading';
import { MemberConfirmationModal } from '@bo-components/members/modal';
import RemoveIcon from '@library/assets/images/icons/x-red-icon.svg';
import { Spinner } from '@library/components/Spinner';
import { useMemberManageGroupActions } from '@bo-hooks/members';
import { useMemo } from 'react';

// Delete, confirm, and loading button icons shown to remove a member group
export interface MemberGroupRemovalButtonsProps {
  id: string;
  isConfirmRemoveIcons: boolean;
  onHideConfirmIcons: () => void;
  onRemoveGroup: (id?: string) => () => void;
  onShowConfirmIcons: (id: string) => () => void;
  showSpinner: boolean;
}

const MemberGroupRemovalButtons = ({
  id,
  isConfirmRemoveIcons,
  onHideConfirmIcons,
  onRemoveGroup,
  onShowConfirmIcons,
  showSpinner
}: MemberGroupRemovalButtonsProps) => {
  if (isConfirmRemoveIcons && showSpinner) {
    return (
      <div className="relative ml-auto h-5">
        <Spinner className="absolute right-0.5 top-0.5" />
      </div>
    );
  }

  if (isConfirmRemoveIcons) {
    return (
      <>
        <Button className="ml-auto" onClick={onRemoveGroup(id)}>
          <img
            src={GreenCheckIcon}
            className="size-5"
            alt="Confirm Remove Group"
          />
        </Button>
        <Button className="ml-2" onClick={onHideConfirmIcons}>
          <img src={RemoveIcon} className="size-5" alt="Cancel Remove Group" />
        </Button>
      </>
    );
  }

  return (
    <Button className="ml-auto" onClick={onShowConfirmIcons(id)}>
      <img src={GrayTrashIcon} className="size-5" alt="Remove Group" />
    </Button>
  );
};

// Modal to manage member groups
export interface MemberManageGroupsModalProps {
  memberId: string;
  onClose: () => void;
}

export const MemberManageGroupsModal = ({
  memberId,
  onClose
}: MemberManageGroupsModalProps) => {
  const {
    addMemberGroupOptions,
    assignedMemberGroups,
    confirmRemoveGroupId,
    isLoadingActiveGroups,
    isLoadingAddRemoveGroups,
    onAddGroup: handleAddGroup,
    onHideConfirmIcons: handleHideConfirmIcons,
    onRemoveGroup: handleRemoveGroup,
    onShowConfirmIcons: handleShowConfirmIcons,
    showSpinner
  } = useMemberManageGroupActions({ memberId });

  const AssignedMemberGroups = useMemo(() => {
    if (!assignedMemberGroups?.length) {
      return (
        <p className="text-soClean-100 text-sm">
          {MANAGE_MEMBER_GROUPS_NO_GROUPS}
        </p>
      );
    }

    return assignedMemberGroups.map(({ id, name }) => (
      <div
        key={id}
        className="bg-soFresh-120 border-soFresh-140 flex h-10 items-center rounded-md border p-2 pl-3"
      >
        <p className="text-soClean-100 text-sm capitalize">{name}</p>
        <MemberGroupRemovalButtons
          id={id}
          isConfirmRemoveIcons={confirmRemoveGroupId === id}
          onHideConfirmIcons={handleHideConfirmIcons}
          onRemoveGroup={handleRemoveGroup}
          onShowConfirmIcons={handleShowConfirmIcons}
          showSpinner={showSpinner}
        />
      </div>
    ));
  }, [
    assignedMemberGroups,
    confirmRemoveGroupId,
    handleHideConfirmIcons,
    handleRemoveGroup,
    handleShowConfirmIcons,
    showSpinner
  ]);

  return (
    <MemberConfirmationModal
      cancelCopy="Cancel"
      childrenClassName="justify-evenly min-h-32"
      className="w-[25rem]"
      confirmationCopy="Save"
      handleConfirm={onClose}
      isOpen
      label={MANAGE_MEMBER_GROUPS_TITLE}
      onCancelModal={onClose}
      showConfirmationMessage
    >
      {isLoadingActiveGroups ? (
        <Loading loadingMessage="Loading Member Groups" />
      ) : (
        <div className="no-scrollbar max-h-[50vh] overflow-y-auto">
          <Combobox
            closeMenuOnSelect
            disabled={isLoadingAddRemoveGroups}
            id="memberManageGroups"
            isMulti={false}
            label="Add new group"
            labelClassName="text-soClean-180 text-sm leading-8"
            onChange={handleAddGroup}
            optionClassName="font-heebo text-sm px-3 py-1 rounded bg-soFresh-110 capitalize"
            options={addMemberGroupOptions}
            placeholder="Select group"
            value={null}
          />
          <div className="flex flex-col gap-2">
            <p className="text-soClean-180 pt-6 text-sm">Remove groups</p>
            {AssignedMemberGroups}
          </div>
        </div>
      )}
    </MemberConfirmationModal>
  );
};
